import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, effect, ElementRef, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NotificationView } from '@api-proxies';
import { NotificationService } from '@http/clients/notification.service';
import { UserOrganizationStorageService } from '@services/user-organization-storage.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import { icons, IconSanitizerService } from 'ngx-lib';
import { NotificationsListComponent } from './notifications-list/notifications-list.component';

@Component({
    selector: 'app-notifications-panel',
    templateUrl: './notifications-panel.component.html',
    styleUrls: ['./notifications-panel.component.scss'],
    imports: [CommonModule, ...CDK_IMPORTS, NotificationsListComponent],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class NotificationsPanelComponent {
    @ViewChildren('menuItem') menuItems!: QueryList<ElementRef>;
    @ViewChildren('seeAll') seeAll!: QueryList<ElementRef>;

    hasUnread?: boolean;
    latestNotifications?: NotificationView[];
    notificationIcon?: SafeHtml;
    emptyNotificationIcon?: SafeHtml;
    isLoading: boolean;
    opened: boolean;

    readonly icons = icons;

    protected scrollStrategy: ScrollStrategy;

    constructor(
        private readonly router: Router,
        private readonly overlay: Overlay,
        private readonly notificationService: NotificationService,
        private readonly iconSanitizer: IconSanitizerService,
        private readonly userOrganizationStorageService: UserOrganizationStorageService
    ) {
        this.hasUnread = false;
        this.isLoading = false;
        this.opened = false;
        this.scrollStrategy = this.overlay.scrollStrategies.close();

        // React to organization changes with effect
        effect(() => {
            const org = this.userOrganizationStorageService.selectedOrganization();
            if (org) {
                this.fetchNotifications();
            }
        });
    }

    togglePanel(event: Event) {
        event.stopPropagation();
        this.opened = !this.opened;
        if (this.opened) {
            this.fetchNotifications();
            setTimeout(() => {
                const firstItem = this.menuItems.first;
                const seeAll = this.seeAll.first;
                if (this.latestNotifications && this.latestNotifications.length > 0) {
                    firstItem.nativeElement.focus();
                } else {
                    seeAll.nativeElement.focus();
                }
            }, 0);
        }
    }

    async fetchNotifications(): Promise<void> {
        this.notificationIcon = this.iconSanitizer.getIcon('notifications');
        this.emptyNotificationIcon = this.iconSanitizer.getIcon('emptyNotifications');
        this.latestNotifications = await this.notificationService.getLatest();
        this.updateUnreadStatus();
    }

    async markAllAsRead(): Promise<void> {
        if (this.hasUnread) {
            this.isLoading = true;
            await this.notificationService.markAllAsRead();
            this.isLoading = false;

            this.latestNotifications?.forEach((notification) => {
                if (!notification.isRead) {
                    notification.isRead = true;
                }
            });
            this.updateUnreadStatus();
        }
        this.closePanel();
    }

    seeAllNotifications(): void {
        if (this.router.url === '/notifications') return;
        this.router.navigate(['/notifications']);
        this.closePanel();
    }

    updateUnreadStatus(): void {
        this.hasUnread =
            this.latestNotifications &&
            this.latestNotifications.length > 0 &&
            this.latestNotifications?.some((notification) => !notification.isRead);
    }

    private closePanel() {
        this.opened = false;
    }
}
